import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, Hourglass, Crown, EyeOff, Swords, Megaphone, Star } from "lucide-react";
import api from "@/lib/api";
import { CLASS_META, CLASS_IMAGE } from "@/lib/game";
import PublicShell from "@/components/PublicShell";
import { usePageMeta } from "@/lib/seo";
import { BlockButton } from "@/components/BlockButton";

function StatStars({ value }) {
  const stars = value <= 50 ? 0.5 : Math.min(10, Math.ceil(value / 50) * 0.5);
  const fullStars = Math.floor(stars);
  const hasHalfStar = stars % 1 !== 0;
  
  return (
    <div className="flex items-center gap-0.5 flex-wrap shrink-0" data-testid={`stat-stars-${value}`}>
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} size={12} className="text-amber-400 fill-amber-400 shrink-0" />
      ))}
      {hasHalfStar && (
        <div className="relative shrink-0">
          <Star size={12} className="text-amber-400" />
          <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
            <Star size={12} className="text-amber-400 fill-amber-400" />
          </div>
        </div>
      )}
      {[...Array(10 - Math.ceil(stars))].map((_, i) => (
        <Star key={`empty-${i}`} size={12} className="text-zinc-700 shrink-0" />
      ))}
    </div>
  );
}

function PublicStatDisplay({ skills }) {
  if (!skills) return null;
  
  const stats = [
    { key: 'strength', label: 'Strength', value: skills.strength || 0 },
    { key: 'stamina', label: 'Stamina', value: skills.stamina || 0 },
    { key: 'speed', label: 'Speed', value: skills.speed || 0 },
    { key: 'technique', label: 'Technique', value: skills.technique || 0 },
    { key: 'precision', label: 'Precision', value: skills.precision || 0 },
    { key: 'defense', label: 'Defense', value: skills.defense || 0 },
  ];

  return (
    <div className="space-y-2" data-testid="public-stat-display">
      {stats.map(({ key, label, value }) => (
        <div key={key} className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm text-zinc-300 min-w-[80px] shrink-0">{label}</span>
          <StatStars value={value} />
        </div>
      ))}
    </div>
  );
}

export default function PublicGladiator() {
  const { id } = useParams();
  const [g, setG] = useState(null);

  useEffect(() => {
    api.get(`/public/gladiator/${id}`).then((r) => setG(r.data)).catch(() => setG(false));
  }, [id]);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/g/${id}` : undefined;
  const title = g && g.name ? `${g.name} — ${g.cls} | Lanista Manager` : "Gladiator | Lanista Manager";
  const rec = g && g.name ? `${g.wins}W/${g.losses}L, rating ${g.rating}` : "";
  const description = g && g.name
    ? `${g.name}, a ${g.cls} (age ${g.age}) from ${g.owner_name}'s ludus — ${rec}${g.potential_grade ? `, potential ${g.potential_grade}` : ""}. View this gladiator's full combat record on Lanista Manager, the free online gladiator management game.`
    : "View this gladiator's record on Lanista Manager, the free online gladiator management game.";
  const jsonLd = g && g.name ? {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "name": title,
    "description": description,
    "url": canonical,
    "mainEntity": {
      "@type": "Person",
      "name": g.name,
      "description": `${g.cls} gladiator`,
      "jobTitle": g.cls,
      "memberOf": {
        "@type": "Organization",
        "name": `${g.owner_name}'s Ludus`
      }
    },
    "about": { "@type": "Person", "name": g.name, "description": `${g.cls} gladiator` },
    "isPartOf": { "@type": "WebSite", "name": "Lanista Manager", "url": canonical ? window.location.origin : undefined },
  } : null;
  usePageMeta({ title, description, canonical, jsonLd });

  return (
    <PublicShell>
      {g === null ? (
        <p className="text-center text-zinc-500 py-20 font-cinzel">Summoning the gladiator…</p>
      ) : g === false ? (
        <div className="text-center py-20">
          <p className="text-zinc-400 mb-6">This gladiator could not be found in the records.</p>
          <Link to="/login" className="font-cinzel uppercase tracking-wide text-sm bg-amber-600 text-zinc-950 px-5 py-2.5 hover:bg-amber-500 transition-colors">Enter the Arena</Link>
        </div>
      ) : (
        <Card g={g} />
      )}
    </PublicShell>
  );
}

function Card({ g }) {
  const meta = CLASS_META[g.cls] || {};
  const img = CLASS_IMAGE[g.cls];
  const total = g.wins + g.losses;
  const winRate = total ? Math.round((g.wins / total) * 100) : 0;

  return (
    <div data-testid="public-gladiator" className="relative">
      {/* Textured background layers */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Noise/grain texture */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />
        
        {/* STRONG diagonal scratch/wear marks - KEPT ENHANCED */}
        <div className="absolute inset-0 opacity-[0.25]" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 100px, rgba(200, 200, 200, 0.3) 100px, rgba(200, 200, 200, 0.3) 102px),
                           repeating-linear-gradient(-45deg, transparent, transparent 150px, rgba(200, 200, 200, 0.15) 150px, rgba(200, 200, 200, 0.15) 152px),
                           repeating-linear-gradient(60deg, transparent, transparent 80px, rgba(180, 180, 180, 0.25) 80px, rgba(180, 180, 180, 0.25) 82px),
                           repeating-linear-gradient(120deg, transparent, transparent 120px, rgba(160, 160, 160, 0.2) 120px, rgba(160, 160, 160, 0.2) 122px)`
        }} />
        
        {/* Subtle arena stone texture */}
        <div className="absolute inset-0 opacity-[0.1]" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(0, 0, 0, 0.08) 0%, transparent 50%),
                           radial-gradient(circle at 50% 80%, rgba(255, 255, 255, 0.03) 0%, transparent 40%)`,
          backgroundSize: '300px 300px, 400px 400px, 350px 350px'
        }} />
        
        {/* Blood splatters */}
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: `radial-gradient(ellipse at 20% 30%, rgba(139, 0, 0, 0.4) 0%, transparent 50%),
                           radial-gradient(ellipse at 80% 60%, rgba(139, 0, 0, 0.3) 0%, transparent 40%),
                           radial-gradient(ellipse at 40% 80%, rgba(139, 0, 0, 0.35) 0%, transparent 45%),
                           radial-gradient(ellipse at 70% 20%, rgba(139, 0, 0, 0.25) 0%, transparent 35%),
                           radial-gradient(ellipse at 15% 70%, rgba(139, 0, 0, 0.3) 0%, transparent 40%),
                           radial-gradient(circle at 50% 50%, rgba(139, 0, 0, 0.2) 0%, transparent 60%)`,
          backgroundSize: '800px 800px, 600px 600px, 700px 700px, 500px 500px, 650px 650px, 1000px 1000px',
          backgroundPosition: '0% 0%, 100% 50%, 50% 100%, 80% 10%, 20% 80%, 50% 50%',
          filter: 'blur(2px)'
        }} />
        
        {/* Vignette effect to draw focus */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.15) 100%)'
        }} />
      </div>
      
      <div className="relative overflow-hidden border border-amber-800/40 bg-zinc-950 mb-6">
        {img && (
          <div className="absolute inset-0">
            <img src={img} alt={g.cls} className="w-full h-full object-cover object-top opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />
          </div>
        )}
        <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
          {img && <img src={img} alt={g.cls} className="w-28 h-28 sm:w-36 sm:h-36 object-cover object-top border-2 border-amber-700/50 shrink-0 shadow-lg" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {meta.icon && <meta.icon className={meta.color} size={18} />}
              <span className="text-[11px] uppercase tracking-widest text-zinc-400">{g.cls}{g.class_title ? ` · ${g.class_title}` : ""}</span>
            </div>
            <h1 className="font-cinzel text-3xl sm:text-4xl text-amber-300 uppercase tracking-wide truncate">{g.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-zinc-500">Lanista: <span className="text-zinc-300">{g.owner_name}</span></p>
              {g.owner_user_id && <BlockButton userId={g.owner_user_id} userName={g.owner_name} />}
            </div>
            {g.owner_campaign_title && (
              <p className="text-sm text-amber-200/90 mt-1 flex items-center gap-1.5 font-cinzel">
                <Crown size={14} className="text-amber-500" /> {g.owner_campaign_title}
              </p>
            )}
            {g.battle_cry_text && (
              <p data-testid="public-battle-cry" className="mt-1.5 text-sm text-amber-200/90 italic flex items-center gap-1.5">
                <Megaphone size={13} className="text-amber-500/80 shrink-0" /> “{g.battle_cry_text}”
              </p>
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
              <span className="flex items-center gap-1.5 text-zinc-300"><Trophy size={14} className="text-amber-500" /> Rating <b className="text-amber-300">{g.rating}</b></span>
              {g.highest_rank && g.highest_rank > 0 && (
                <span className="flex items-center gap-1.5 text-zinc-300">
                  <Crown size={14} className="text-amber-500" /> Highest Rank <b className="text-amber-300">#{g.highest_rank}</b>
                </span>
              )}
              <span className="flex items-center gap-1.5 text-zinc-300"><Hourglass size={14} className="text-zinc-500" /> Age <b>{g.age}</b></span>
              <span className="text-zinc-300">Record <b className="text-emerald-400">{g.wins}W</b> / <b className="text-red-400">{g.losses}L</b> <span className="text-zinc-500">({winRate}%)</span></span>
              {g.decisive?.decisive_pct != null && (
                <span data-testid="public-decisive-pct" className="text-zinc-300" title={`${g.decisive.decisive_wins} of ${g.decisive.total_wins} wins settled in under 10 rounds`}>Decisive Wins <b className="text-orange-400">{g.decisive.decisive_pct}%</b> <span className="text-zinc-500">({g.decisive.decisive_wins})</span></span>
              )}
              {g.potential_grade && <span className="text-zinc-300">Potential <b className="text-violet-400">{g.potential_grade}</b></span>}
              {g.trophies > 0 && <span className="flex items-center gap-1.5 text-amber-300"><Crown size={14} className="text-amber-500" /> {g.trophies} {g.trophies === 1 ? "trophy" : "trophies"}</span>}
            </div>
            {g.retired && <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-3">Retired — a legend at rest</p>}
          </div>
        </div>
      </div>

      <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">Combat Skills</h2>
      {g.show_skills && g.skills ? (
        <div className="bg-zinc-950 border border-amber-900/30 p-5 mb-8">
          <PublicStatDisplay skills={g.skills} />
        </div>
      ) : (
        <div className="bg-zinc-950 border border-dashed border-amber-900/40 p-8 text-center mb-8">
          <EyeOff size={22} className="text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">This lanista keeps {g.name}'s skills out of public view.</p>
        </div>
      )}

      <CTA name={g.name} />
    </div>
  );
}

function CTA({ name }) {
  return (
    <div className="border border-amber-700/40 bg-gradient-to-br from-amber-950/30 to-zinc-950 p-6 text-center" data-testid="public-cta">
      <h3 className="font-cinzel text-xl sm:text-2xl text-amber-300 uppercase tracking-wide mb-2">Think you can do better?</h3>
      <p className="text-sm text-zinc-400 max-w-md mx-auto mb-5">Recruit your own gladiators, train them in real time, and challenge lanistae like {name}'s owner in the arena. It's free to play.</p>
      <Link to="/login" data-testid="public-signup-cta"
        className="inline-flex items-center gap-2 font-cinzel uppercase tracking-wide text-sm bg-amber-600 text-zinc-950 px-6 py-3 hover:bg-amber-500 transition-colors">
        <Swords size={16} /> Create Your Ludus
      </Link>
    </div>
  );
}
